import React, { useState, useRef } from 'react';
import { CHART_PALETTE, SURFACE } from './chartColors';

interface BarDataItem {
  label: string;
  value: number;
}

interface PremiumBarChartProps {
  data: BarDataItem[];
  height?: number;
  colorIndex?: number; // Use a single color from the palette for all bars
  multiColor?: boolean; // Different color for each bar
  horizontal?: boolean;
  showValues?: boolean;
  formatValue?: (value: number) => string;
  className?: string;
  maxBars?: number;
}

const PremiumBarChart: React.FC<PremiumBarChartProps> = ({
  data,
  height = 280,
  colorIndex = 0,
  multiColor = false,
  horizontal = false,
  showValues = true,
  formatValue = (v) => v.toLocaleString(),
  className = '',
  maxBars,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string; value: string } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayData = maxBars ? data.slice(0, maxBars) : data;
  const maxValue = Math.max(...displayData.map(d => d.value), 1);

  const handleMouseMove = (e: React.MouseEvent, item: BarDataItem, index: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 12,
      label: item.label,
      value: formatValue(item.value),
    });
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setTooltip(null);
    setHoveredIndex(null);
  };

  if (displayData.length === 0) {
    return <div className="flex items-center justify-center h-full text-gray-400">No data</div>;
  }

  const getColor = (i: number) => {
    if (multiColor) return CHART_PALETTE[i % CHART_PALETTE.length];
    return CHART_PALETTE[colorIndex % CHART_PALETTE.length];
  };

  // Grid lines (5 lines)
  const gridSteps = 4;
  const gridValues = Array.from({ length: gridSteps + 1 }, (_, i) => Math.round((maxValue / gridSteps) * i));

  if (horizontal) {
    const barHeight = Math.min(32, Math.max(18, (height - 24) / displayData.length - 8));
    const totalHeight = Math.max(height, displayData.length * (barHeight + 8) + 24);

    return (
      <div ref={containerRef} className={`relative ${className}`} style={{ height: totalHeight }}>
        <div className="flex flex-col gap-1.5" style={{ height: totalHeight - 24 }}>
          {displayData.map((item, i) => {
            const pct = (item.value / maxValue) * 100;
            const color = getColor(i);
            const isHovered = hoveredIndex === i;

            return (
              <div
                key={i}
                className="flex items-center gap-3 cursor-pointer group"
                onMouseMove={(e) => handleMouseMove(e, item, i)}
                onMouseLeave={handleMouseLeave}
                style={{ height: barHeight + 8 }}
              >
                <div
                  className="text-xs text-right flex-shrink-0"
                  style={{
                    width: 140,
                    color: isHovered ? SURFACE.text : SURFACE.textSecondary,
                    fontWeight: isHovered ? 600 : 400,
                    transition: 'all 0.15s ease',
                  }}
                  title={item.label}
                >
                  {item.label}
                </div>
                <div className="flex-1 relative" style={{ height: barHeight }}>
                  {/* Background track */}
                  <div
                    className="absolute inset-0 rounded-r-md"
                    style={{ background: SURFACE.borderLight }}
                  />
                  {/* Bar */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-r-md"
                    style={{
                      width: `${Math.max(pct, 1)}%`,
                      background: `linear-gradient(90deg, ${color.gradient[0]}, ${color.gradient[1]})`,
                      opacity: hoveredIndex !== null && !isHovered ? 0.5 : 1,
                      transform: isHovered ? 'scaleY(1.08)' : 'scaleY(1)',
                      transition: 'all 0.2s ease',
                      boxShadow: isHovered ? `0 2px 8px ${color.base}40` : 'none',
                    }}
                  />
                </div>
                {showValues && (
                  <div
                    className="text-xs font-semibold flex-shrink-0 tabular-nums"
                    style={{
                      width: 48,
                      color: isHovered ? color.dark : SURFACE.textSecondary,
                      transition: 'color 0.15s ease',
                    }}
                  >
                    {formatValue(item.value)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="absolute pointer-events-none z-10"
            style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
          >
            <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap">
              <div className="font-semibold">{tooltip.label}</div>
              <div className="text-gray-300 mt-0.5">{tooltip.value}</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vertical bars
  const barWidth = Math.min(48, Math.max(16, 600 / displayData.length - 12));
  const chartPaddingTop = 24;
  const chartPaddingBottom = 48;
  const chartHeight = height - chartPaddingTop - chartPaddingBottom;

  return (
    <div ref={containerRef} className={`relative ${className}`} style={{ height }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${Math.max(displayData.length * (barWidth + 12) + 40, 300)} ${height}`} preserveAspectRatio="xMidYMid meet">
        {/* Gradient defs */}
        {displayData.map((_, i) => {
          const color = getColor(i);
          return (
            <defs key={`grad-${i}`}>
              <linearGradient id={`bar-grad-${colorIndex}-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color.gradient[0]} />
                <stop offset="100%" stopColor={color.gradient[1]} />
              </linearGradient>
            </defs>
          );
        })}

        {/* Grid lines */}
        {gridValues.map((val, i) => {
          const y = chartPaddingTop + chartHeight - (val / maxValue) * chartHeight;
          return (
            <g key={`grid-${i}`}>
              <line
                x1={30}
                x2={displayData.length * (barWidth + 12) + 30}
                y1={y}
                y2={y}
                stroke={SURFACE.gridLine}
                strokeWidth={1}
              />
              <text x={26} y={y + 3} textAnchor="end" fill={SURFACE.textMuted} fontSize={11}>
                {val}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {displayData.map((item, i) => {
          const barH = (item.value / maxValue) * chartHeight;
          const x = 36 + i * (barWidth + 12);
          const y = chartPaddingTop + chartHeight - barH;
          const isHovered = hoveredIndex === i;

          return (
            <g
              key={i}
              onMouseMove={(e) => {
                const svgRect = containerRef.current?.getBoundingClientRect();
                if (svgRect) {
                  setTooltip({
                    x: e.clientX - svgRect.left,
                    y: e.clientY - svgRect.top - 12,
                    label: item.label,
                    value: formatValue(item.value),
                  });
                }
                setHoveredIndex(i);
              }}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: 'pointer' }}
            >
              {/* Bar with rounded top */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barH, 2)}
                rx={4}
                ry={4}
                fill={`url(#bar-grad-${colorIndex}-${i})`}
                style={{
                  opacity: hoveredIndex !== null && !isHovered ? 0.4 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              />
              {/* Value on top */}
              {showValues && barH > 20 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 6}
                  textAnchor="middle"
                  fill={isHovered ? getColor(i).dark : SURFACE.textSecondary}
                  fontSize={12}
                  fontWeight={isHovered ? 700 : 500}
                  style={{ transition: 'fill 0.15s ease' }}
                >
                  {formatValue(item.value)}
                </text>
              )}
              {/* X-axis label */}
              <text
                x={x + barWidth / 2}
                y={chartPaddingTop + chartHeight + 16}
                textAnchor="middle"
                fill={isHovered ? SURFACE.text : SURFACE.textMuted}
                fontSize={10}
                fontWeight={isHovered ? 600 : 400}
              >
                {item.label.length > 12 ? item.label.substring(0, 10) + '..' : item.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none z-10"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
        >
          <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap">
            <div className="font-semibold">{tooltip.label}</div>
            <div className="text-gray-300 mt-0.5">{tooltip.value}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumBarChart;
